/**
 * Сотрудники. Модуль для работы с API.
 * @module employees
 */

import { maxPerPage } from "@/constants";
import { buildQueryParams, cleanParams } from "@/utils";

/**
 * Получение списка сотрудников. Поддерживает пагинацию, поиск и фильтры. Сейчас использует только админ, но ручку на беке расширили
 * @param {Object} params - Параметры запроса
 * @param {boolean} isAdmin - флаг администратора (по умолчанию true, т.к. обычно используется в админке)
 * @returns {Promise} - список сотрудников
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (params, isAdmin = true) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(
      `/api/employees${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение полного списка сотрудников без пагинации (обычно использовался в админке для селектов, но стали использовать и для обычного пользователя для назначения Ресурсам Сотрудников)
 * @param {Object} params - Параметры запроса
 * @param {boolean} isAdmin - флаг администратора (по умолчанию true, т.к. обычно используется в админке)
 * @returns {Promise} - список сотрудников
 * @throws {Error} - ошибка при получении данных
 */
export const getEmployeesOptions = async (params, isAdmin = true) => {
  const cleanedFilters = cleanParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  });
  const queryString = new URLSearchParams(cleanedFilters).toString();

  try {
    const response = await fetch(
      `/api/employees${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получить список сотрудников РГ, НЕобязательный параметр unit_uuid
 * Если unit_uuid не передан, то возвращаются все сотрудники всех моих РГ
 * @param {Object} params - Параметры запроса
 * @param {string} params.unit_uuid - uuid РГ
 * @returns {Promise} - список сотрудников
 * @throws {Error} - ошибка при получении
 */
export const getUnitEmployees = async (params) => {
  const cleanedFilters = cleanParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  });
  const queryString = new URLSearchParams(cleanedFilters).toString();

  try {
    const response = await fetch(`/api/unitmembers?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение данных сотрудника по uuid
 * @param {string} uuid - uuid сотрудника
 * @returns {Promise} - данные сотрудника
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (uuid) => {
  try {
    const response = await fetch(`/api/employee?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.employee;
  } catch (err) {
    throw err;
  }
};

/**
 *  Создание сотрудника
 * @param {object} item - данные сотрудника
 * @returns {Promise} - созданный сотрудник
 * @throws {Error} - ошибка при создании данных
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/employee", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.employee;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление сотрудника
 * @param {object} item - данные сотрудника
 * @returns {Promise} - обновленный сотрудник
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/employee", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.employee;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление сотрудника
 * @param {object} item - данные сотрудника
 * @returns {Promise} - удаленный сотрудник
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/employee", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.employee;
  } catch (err) {
    throw err;
  }
};

/**
 * Добавление аватара пользователю
 * @param {object} formData - данные аватара, передаются как FormData
 * @returns {Promise} - добавленный аватар
 * @throws {Error} - ошибка при добавлении аватара
 */
export const updateAvatar = async (formData) => {
  try {
    const response = await fetch("/api/employee/avatar", {
      method: "POST",
      body: formData,
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при добавлении аватара. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.employee;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление аватара пользователю
 * @returns {Promise} - удаленный аватар
 * @throws {Error} - ошибка при удалении аватара
 */
export const deleteAvatar = async () => {
  try {
    const response = await fetch("/api/employee/avatar", {
      method: "DELETE",
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении аватара. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.employee;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка РГ сотрудника
 * @param {string} uuid - uuid сотрудника
 * @returns {Promise} - список РГ сотрудника
 * @throws {Error} - ошибка при получении данных
 */
export const getEmployeeUnits = async (uuid) => {
  try {
    const response = await fetch(
      `/api/employee/units/admin?employee_uuid=${uuid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.units;
  } catch (err) {
    throw err;
  }
};
